import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Filter, X } from "lucide-react";
import VehicleCard from "../components/site/VehicleCard";
import { listVehicles, listMakes } from "../lib/api";

const bodyTypes = ["sedan", "suv", "coupe", "truck", "convertible"];
const fuelTypes = ["gasoline", "diesel", "electric", "hybrid"];

export default function Inventory() {
  const [vehicles, setVehicles] = useState([]);
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    make: "",
    body_type: "",
    fuel_type: "",
    min_price: "",
    max_price: "",
    min_year: "",
  });

  useEffect(() => {
    listMakes().then(setMakes).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null) params[k] = v;
    });
    listVehicles(params)
      .then(setVehicles)
      .finally(() => setLoading(false));
  }, [filters]);

  const reset = () => setFilters({ make: "", body_type: "", fuel_type: "", min_price: "", max_price: "", min_year: "" });

  const activeCount = useMemo(() => Object.values(filters).filter((v) => v !== "").length, [filters]);

  return (
    <div data-testid="page-inventory" className="pt-32 max-w-[1500px] mx-auto px-6 md:px-10 pb-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <div className="overline">Inventory</div>
        <h1 className="font-display text-6xl md:text-8xl mt-3 tracking-tighter">
          Every car,
          <br />
          <span className="text-[var(--primary)]">on the floor.</span>
        </h1>
        <p className="text-white/60 mt-6 max-w-xl">
          {vehicles.length} vehicles available. Filter by make, body style, and price range to find your next ride.
        </p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters */}
        <aside className="glass rounded-2xl p-6 h-fit sticky top-28" data-testid="inventory-filters">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Filter size={16} />
              <span className="overline">Filters</span>
              {activeCount > 0 && (
                <span className="text-[10px] bg-[var(--primary)] text-white rounded-full px-2 py-[2px]">{activeCount}</span>
              )}
            </div>
            {activeCount > 0 && (
              <button onClick={reset} data-testid="filter-reset" className="text-xs text-white/50 hover:text-white flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            )}
          </div>

          <div className="space-y-5">
            <div>
              <label>Make</label>
              <select data-testid="filter-make" value={filters.make} onChange={(e) => setFilters({ ...filters, make: e.target.value })}>
                <option value="">Any make</option>
                {makes.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Body</label>
              <select data-testid="filter-body" value={filters.body_type} onChange={(e) => setFilters({ ...filters, body_type: e.target.value })}>
                <option value="">Any body</option>
                {bodyTypes.map((b) => (
                  <option key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Fuel</label>
              <select data-testid="filter-fuel" value={filters.fuel_type} onChange={(e) => setFilters({ ...filters, fuel_type: e.target.value })}>
                <option value="">Any fuel</option>
                {fuelTypes.map((b) => (
                  <option key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label>Min $</label>
                <input data-testid="filter-min-price" type="number" placeholder="0" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} />
              </div>
              <div>
                <label>Max $</label>
                <input data-testid="filter-max-price" type="number" placeholder="500000" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} />
              </div>
            </div>
            <div>
              <label>Min year</label>
              <input data-testid="filter-min-year" type="number" placeholder="2018" value={filters.min_year} onChange={(e) => setFilters({ ...filters, min_year: e.target.value })} />
            </div>
          </div>
        </aside>

        {/* Results */}
        <div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="inventory-loading">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="vehicle-card animate-pulse">
                  <div className="aspect-[4/3] bg-white/5" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-white/5 w-1/3" />
                    <div className="h-6 bg-white/5 w-2/3" />
                    <div className="h-3 bg-white/5 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : vehicles.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center" data-testid="inventory-empty">
              <div className="overline">No matches</div>
              <h3 className="font-display text-3xl mt-3">Try widening your filters.</h3>
              <button onClick={reset} className="btn-outline mt-6">Reset filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="inventory-grid">
              {vehicles.map((v, i) => (
                <VehicleCard key={v.id} vehicle={v} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
